
import Vue from 'vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import { CHECKOUT_TEAM_PLAN_SUBSCRIPTION } from '@run-diffusion/shared';
import { TeamUserMixin } from '@/mixins';
import { ROUTER } from '@/router/constants';
import {
	COMPANY_DIRECTORY_TITLE,
	COMPANY_NAV_SECTION,
	CompanyNavSection,
	companyNavSection,
} from './companyNavState';

interface CompanyNavItem {
	section: CompanyNavSection;
	label: string;
	icon: string;
	to: { name: string };
}

/**
 * The company area's own navigation, beside every page under `/team`: the
 * dashboard, its settings, the directory, and the company's subscription. It
 * sits on the page rather than in a panel of its own, so it reads as a part of
 * the company pages and not as a second app sidebar. TeamPage lays it out: a
 * column on wide screens, a row of pills (`strip`) above the page otherwise.
 *
 * The item for the current section is marked, whichever page of it is open
 * (`companyNavSection`), which keeps it in step with the breadcrumbs: every
 * trail starts at Company Dashboard and runs through the section the nav marks.
 */
export default Vue.extend({
	name: 'CompanyNav',
	mixins: [
		TeamUserMixin,
	],
	props: {
		// A row of pills above the page, for widths without room for a column.
		strip: { type: Boolean, default: false },
	},
	computed: {
		...mapState([
			'team',
		]),
		...mapGetters([
			'showStripeTeamSubscriptionPortalBtn',
			'companyClubOfferBenefits',
		]),
		companyName (): string {
			const company = this.team && typeof this.team.company === 'object' ? this.team.company : null;
			return (company && company.name) || '';
		},
		activeSection (): CompanyNavSection {
			return companyNavSection(this.$route);
		},
		pageItems (): CompanyNavItem[] {
			return [
				{
					section: COMPANY_NAV_SECTION.DASHBOARD,
					label: 'Company Dashboard',
					icon: 'mdi-domain',
					to: { name: ROUTER.COMPANY_DASHBOARD },
				},
				// The same people the old Company Settings button was shown to
				...(this.hasManageCompanyPermission || this.hasManageCompanyTeamRolesPermission ? [
					{
						section: COMPANY_NAV_SECTION.SETTINGS,
						label: 'Company Settings',
						icon: 'mdi-cog-outline',
						to: { name: ROUTER.COMPANY_SETTINGS },
					},
				] : []),
				{
					section: COMPANY_NAV_SECTION.DIRECTORY,
					label: COMPANY_DIRECTORY_TITLE,
					icon: 'mdi-account-group-outline',
					to: { name: ROUTER.MEMBERS_DIRECTORY },
				},
			];
		},
		// The same people the old subscription button was shown to, and only when
		// clicking it does something: there is a Stripe customer to send them to,
		// or an enterprise plan to manage.
		showSubscription (): boolean {
			return !!(
				this.hasManageCompanyPermission &&
				(this.showStripeTeamSubscriptionPortalBtn || this.companyClubOfferBenefits.isEnterprise)
			);
		},
		// Stripe's portal replaces the page, and checkout opens a new tab; an enterprise plan opens a dialog here.
		subscriptionLeavesApp (): boolean {
			return !!this.showStripeTeamSubscriptionPortalBtn;
		},
	},
	methods: {
		...mapActions([
			'goToStripeSubscriptionPortal',
			'setUpsellDialog',
		]),
		// Moved here unchanged from the company dashboard's "Manage subscription / payment methods" button.
		onSubscriptionClick () {
			if (this.showStripeTeamSubscriptionPortalBtn) {
				if (this.isTeamActive) {
					this.goToStripeSubscriptionPortal(this.team);
				} else {
					const routeData = this.$router.resolve({
						name: 'GoToPayment',
						query: {
							checkoutAction: CHECKOUT_TEAM_PLAN_SUBSCRIPTION,
						},
					});
					window.open(routeData.href, '_blank');
				}
			} else if (this.companyClubOfferBenefits.isEnterprise) {
				this.setUpsellDialog({ manageEnterprisePlan: true });
			}
		},
	},
});
